"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Image from "@/helpers/Image";
import { CTAButton } from "@/shadcn/ui/button";
import { PageEcommerceSchema } from "@/sanity/pages/PageEcommerceTemplate";

export default function FounderSection({
  data,
}: {
  data: PageEcommerceSchema;
}) {
  if (!data.founderTitle && !data.founderImage) return null;

  return (
    <section className="w-full bg-[#f7f7f7] pt-[7rem] pb-[7rem] lg:pt-[12rem] lg:pb-[12rem]">
      <div className="mx-auto w-[min(150rem,100%)] px-[3rem] lg:px-0 flex flex-col gap-[3rem] lg:gap-[12rem]">
        {/* Centered header */}
        <div className="flex flex-col items-center text-center gap-[2rem] mx-auto">
          {data.founderChip && (
            <p className="mb-0 font-blacker italic text-[2rem] leading-[1.5] text-black/70">
              {data.founderChip}
            </p>
          )}
          {data.founderTitle && (
            <div className="richtext richtext-center prose-headings:font-medium prose-headings:text-[5rem] lg:prose-headings:text-[6rem] prose-headings:leading-[1.1] prose-headings:tracking-[-0.3rem] prose-headings:text-black prose-headings:mb-[2.25rem] prose-p:max-w-[102.5rem]">
              <PortableText
                value={data.founderTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-[6rem] lg:gap-[11.625rem]">
          {/* Left: subtitle + credentials */}
          <div className="flex flex-col gap-[4rem] lg:gap-[7rem] lg:w-[73.875rem] shrink-0">
            {/* Mobile image */}
            <div className="flex flex-col gap-[1.5rem] lg:hidden">
              {data.founderImage && (
                <div className="lg:hidden w-full">
                  <Image
                    src={data.founderImage}
                    alt={data.founderPersonName ?? ""}
                    className="w-full"
                  />
                </div>
              )}
              {(data.founderPersonName || data.founderPersonRole) && (
                <div className="lg:hidden flex flex-col gap-[0.75rem]">
                  {data.founderPersonName && (
                    <p className="mb-0 font-medium text-[3.5rem] leading-[1.2] tracking-[-0.09rem] text-black">
                      {data.founderPersonName}
                    </p>
                  )}
                  {data.founderPersonRole && (
                    <p className="mb-0 font-geist-mono text-[1.75rem] font-medium leading-[1.33] tracking-[0.06rem] uppercase text-[#228E71]">
                      {data.founderPersonRole}
                    </p>
                  )}
                </div>
              )}
            </div>
            {data.founderSubtitle && (
              <p className="mb-0 font-medium text-[3.4rem] lg:text-[4rem] leading-[1.2] tracking-[-0.12rem] text-black">
                {data.founderSubtitle}
              </p>
            )}

            {!!data.founderCredentials?.length && (
              <div className="flex flex-col">
                {data.founderCredentials.map((cred, i) => (
                  <div key={cred._key ?? i}>
                    {i > 0 && <div className="h-px bg-black/10" />}
                    <div className="flex gap-[4.875rem] items-start py-[2.5rem]">
                      <p className="mb-0 font-spaceMono font-medium text-[#228e71] text-[1.75rem] leading-none tracking-[-0.0525rem] shrink-0 w-[3rem] pt-[0.5rem]">
                        {String(i + 1).padStart(2, "0")}
                      </p>
                      {cred.text && (
                        <div className="flex-1 richtext richtext-start prose-p:text-[2.25rem] leading-[1.5] text-black/70 [&_strong]:text-black [&_strong]:font-medium">
                          <PortableText
                            value={cred.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/*{data.founderCTA && (
              <CTAButton data={data.founderCTA} className="self-start" />
            )}*/}
          </div>

          {/* Right: portrait + name/role */}
          {data.founderImage && (
            <div className="hidden lg:flex flex-col gap-[3rem] w-full lg:w-[63.75rem] flex-shrink-0">
              <Image
                src={data.founderImage}
                alt={data.founderPersonName ?? ""}
                className="w-full"
              />
              {(data.founderPersonName || data.founderPersonRole) && (
                <div className="flex flex-col gap-[0.75rem]">
                  {data.founderPersonName && (
                    <p className="mb-0 font-medium text-[3.5rem] leading-[1.2] tracking-[-0.09rem] text-black">
                      {data.founderPersonName}
                    </p>
                  )}
                  {data.founderPersonRole && (
                    <p className="mb-0 font-geist-mono text-[1.75rem] font-medium leading-[1.33] tracking-[0.06rem] uppercase text-[#228E71]">
                      {data.founderPersonRole}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
